import type { User } from "firebase/auth";
import type { useManagedCompanies } from "@/auth/useManagedCompanies";
import { findManagedCompanies } from "@/data/firestore/membershipRepository";
import { authService } from "@/lib/authService";
import { useSystemStore } from "@/stores/systemStore";

export function publicOnboardingAllowed(account: ReturnType<typeof useManagedCompanies>) {
  return !account.resolving && !account.adminError && !account.membershipError &&
    (account.isSystemAdmin || !account.currentUser || account.currentUser.isAnonymous ||
      account.companies?.length === 0);
}

/** Recheck after a popup: its returned identity can differ from the launch identity. */
export async function verifyPublicOnboardingUser(user: User): Promise<boolean> {
  const system = await new Promise<ReturnType<typeof useSystemStore.getState> | null>((resolve) => {
    let unsubscribe = () => {};
    const timer = setTimeout(() => { unsubscribe(); resolve(null); }, 25_000);
    const check = () => {
      if (authService.getCurrentUser()?.uid !== user.uid) {
        clearTimeout(timer);
        unsubscribe();
        resolve(null);
        return;
      }
      const state = useSystemStore.getState();
      if (state.uid !== user.uid || !state.adminAccessResolved) return;
      clearTimeout(timer);
      unsubscribe();
      resolve(state);
    };
    unsubscribe = useSystemStore.subscribe(check);
    check();
  });
  if (!system || system.error || authService.getCurrentUser()?.uid !== user.uid) return false;
  if (system.isSystemAdmin) return true;
  try {
    const companies = await findManagedCompanies(user.uid);
    return companies.length === 0 && authService.getCurrentUser()?.uid === user.uid;
  } catch {
    return false;
  }
}
